import { Award, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import DigitalSignatureBlock from '../components/documents/DigitalSignatureBlock';

interface Props {
  data: {
    certificateType: string;
    body: string;
    clinicalDetails?: { label: string; value: string }[];
    conditions?: string[];
    recommendations?: string[];
    validityPeriod?: string;
    signatories: {
      name: string;
      role: string;
      nhcId?: string;
      licenseNo?: string;
      institution?: string;
    }[];
  };
}

export default function CertificateTemplate({ data }: Props) {
  return (
    <div className="space-y-3">
      {/* Certificate Type Badge */}
      <div className="flex items-center gap-1.5 mb-2">
        <Award className="w-3.5 h-3.5 text-gray-700" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-700">
          {data.certificateType}
        </span>
      </div>

      {/* Body */}
      <p className="text-[11px] leading-relaxed text-gray-800">{data.body}</p>

      {/* Clinical Details Table */}
      {data.clinicalDetails && data.clinicalDetails.length > 0 && (
        <div className="a4-section mt-3">
          <h3 className="text-[9px] font-bold uppercase tracking-wider text-gray-600 mb-1.5">
            Clinical Details
          </h3>
          <table className="w-full border-collapse text-[10px]">
            <tbody>
              {data.clinicalDetails.map((detail, i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : ''}>
                  <td className="font-semibold text-gray-700 py-1 px-2 border border-gray-200 w-1/3">
                    {detail.label}
                  </td>
                  <td className="text-gray-800 py-1 px-2 border border-gray-200">
                    {detail.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Conditions */}
      {data.conditions && data.conditions.length > 0 && (
        <div className="a4-section mt-3">
          <div className="flex items-center gap-1 mb-1.5">
            <AlertTriangle className="w-3 h-3 text-gray-600" />
            <h3 className="text-[9px] font-bold uppercase tracking-wider text-gray-600">
              Conditions
            </h3>
          </div>
          <ol className="list-decimal list-inside space-y-0.5 pl-1">
            {data.conditions.map((condition, i) => (
              <li key={i} className="text-[10px] text-gray-700 leading-snug">
                {condition}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Recommendations */}
      {data.recommendations && data.recommendations.length > 0 && (
        <div className="a4-section mt-3">
          <div className="flex items-center gap-1 mb-1.5">
            <CheckCircle className="w-3 h-3 text-gray-600" />
            <h3 className="text-[9px] font-bold uppercase tracking-wider text-gray-600">
              Recommendations
            </h3>
          </div>
          <ul className="space-y-0.5 pl-1">
            {data.recommendations.map((rec, i) => (
              <li key={i} className="text-[10px] text-gray-700 flex items-start gap-1">
                <span className="text-gray-400 mt-0.5">&#8226;</span>
                <span>{rec}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Validity Period */}
      {data.validityPeriod && (
        <div className="a4-section mt-3 flex items-center gap-1 text-[9px] text-gray-600 bg-gray-50 px-2.5 py-1.5 border border-gray-200 rounded">
          <Clock className="w-3 h-3" />
          <span className="font-semibold">Validity:</span>
          <span>{data.validityPeriod}</span>
        </div>
      )}

      {/* Digital Signatures */}
      <DigitalSignatureBlock signatories={data.signatories} />
    </div>
  );
}
